/**
 * Views of the attention block (KAN-734)
 *
 * "Требуют внимания": clients that need a human now; a row not taken to work
 * offers only "Взять в работу", a taken one (back in attention) offers the
 * outcome (KAN-911: "Отработано" only after taking, otherwise no manager is
 * assigned; one rule attentionRowActions). "В работе": clients taken to
 * work whose outcome is not set yet; rows show when they were taken and offer
 * only the outcome. A new view is one more entry here.
 */
import {
  dashboardApi,
  type AttentionItem,
  type AttentionList,
  type AttentionParams,
} from '@/services/api/dashboard'
import { ATTENTION_QUERY_KEY, ATTENTION_WORKED_QUERY_KEY } from '@/services/api/queryKeys'
import { newMessagesFirst } from '@/lib/newMessage'
import { attentionRowActions } from '@/lib/takenToWork'
import { isIdleRow, withWorkContinues } from '@/lib/workContinues'
import { formatAttentionDate } from './attentionFormat'
import type { AttentionAction } from './useAttentionActions'

export type AttentionView = 'attention' | 'worked'

export interface AttentionViewConfig {
  label: string
  queryKey: readonly unknown[]
  fetch: (params: AttentionParams) => Promise<AttentionList>
  /** Row buttons, by the row (KAN-911: depend on taken_at). */
  actions: (item: AttentionItem) => readonly AttentionAction[]
  emptyText: string
  /** Date line of a row. */
  formatMeta: (item: AttentionItem) => string
  metaTestId?: string
  /** Link to the same clients in the clients list, shown under the rows. */
  clientsLink?: { to: string; label: string }
  /** KAN-828: в строках есть выбор менеджера клиента (без закрытия строки). */
  assignManager: boolean
  /** KAN-918: the "Новое сообщение" badge on rows with new_message_at, such rows on top. */
  newMessages: boolean
  /** KAN-926: rows grouped by attention_section with a title and a count. */
  sections: boolean
  /** KAN-960: кнопка "Следующий контакт" в строке. */
  nextContact: boolean
}

/** Worked view: the client is already handled, only the outcome is left. */
const OUTCOME_ACTIONS: readonly AttentionAction[] = ['success', 'refused']

export const ATTENTION_VIEWS: Record<AttentionView, AttentionViewConfig> = {
  attention: {
    label: 'Требуют внимания',
    queryKey: ATTENTION_QUERY_KEY,
    fetch: (params) => dashboardApi.getAttention(params),
    // KAN-955: у строки "Нет движения" ещё "Работа идёт".
    actions: (item) => withWorkContinues(attentionRowActions(item), isIdleRow(item)),
    assignManager: true,
    newMessages: false,
    sections: true,
    nextContact: true,
    emptyText: 'Сейчас ничего не требует внимания',
    formatMeta: (item) => formatAttentionDate(item.since),
  },
  worked: {
    // KAN-852: клиенты, взятые в работу (у бота забрали), исхода ещё нет.
    label: 'В работе',
    queryKey: ATTENTION_WORKED_QUERY_KEY,
    fetch: (params) => dashboardApi.getWorkedAttention(params),
    actions: () => OUTCOME_ACTIONS,
    // KAN-828: менеджер выбран при взятии в работу.
    assignManager: false,
    // KAN-918: взятый клиент написал: пометка и строка сверху (в "Требуют внимания" он не возвращается).
    newMessages: true,
    sections: false,
    nextContact: false,
    emptyText: 'Нет клиентов в работе без исхода',
    clientsLink: { to: '/clients?worked=1', label: 'Все в списке клиентов' },
    formatMeta: (item) => `в работе с ${formatAttentionDate(item.attention_done_at ?? item.since)}`,
    metaTestId: 'attention-worked-at',
  },
}

/** KAN-918: rows in the order of the view (new messages first in "В работе"). */
export function orderAttentionItems(config: AttentionViewConfig, items: readonly AttentionItem[]): AttentionItem[] {
  return config.newMessages ? newMessagesFirst(items) : [...items]
}

export const ATTENTION_VIEW_TABS = (Object.keys(ATTENTION_VIEWS) as AttentionView[]).map(
  (value) => ({ value, label: ATTENTION_VIEWS[value].label })
)
